import AreaMarker from '@/components/CustomComponent/AreaMarker';
import LoadingSpinner from '@/components/CustomComponent/LoadingSpinner';
import ImageAreaMarker from '@/components/CustomComponent/ReactImageAreaMarker';
import { COMMON_TEXT } from '@/helpers/common-text';
import { MEMORY_STORE, QUERY_STRING_STORE } from '@/helpers/constants';
import {
  calculateRectangle,
  transformCoordinateBottomLeftToTopLeft,
} from '@/helpers/utility';
import { format } from 'date-fns';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import '@/components/CustomComponent/ReactImageMarker/image-marker.less';
import {
  getPeopleCountsAreaByFloorId,
  fetchPeopleCountFormat,
  fetchPeopleCountThresholds,
} from './query-request';
import { isEqual } from 'lodash';
import ThresholdLegend from './ThresholdLegend';

@connect(state => ({
  querystring: state.querystring,
  sessionStore: state.session,
}))
class PeopleCounts extends Component {
  constructor(props) {
    super(props);
    this.state = {
      data: [],
      tenant:
        this.props?.querystring?.[QUERY_STRING_STORE.SELECT_BOX_TENANT]?.[0],
      floor:
        this.props?.querystring?.[QUERY_STRING_STORE.SELECT_BOX_FLOOR]?.[0],
      isLoading: false,
      floorMapUrl: '',
      floorMapError: false,
      lastUpdateTs: '',
      isPeopleFormatCount: false,
      areaLegend: [],
      peopleCountThresholds: null,
    };
    this._isMounted = false; // Flag to track the mounted status
    this.tooltipRef = React.createRef();
  }

  validateFloorMapUrl(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(url);
      img.onerror = () => reject(url);
      img.src = url;
    });
  }

  getFloorMapUrl = floorId => {
    const floors = this.props?.sessionStore[MEMORY_STORE.FLOORS] || [];
    const floor = floors.find(floor => floor.floor_id === floorId);
    const floorMapUrl = floor ? floor.floor_map_url : '';
    return floorMapUrl;
  };

  setFloorMap = async floorId => {
    const floors = this.props?.sessionStore[MEMORY_STORE.FLOORS] || [];
    const floor = floors.find(floor => floor.floor_id === floorId);
    const floorMapUrl = floor ? floor.floor_map_url : '';
    try {
      await this.validateFloorMapUrl(floorMapUrl);
      if (this._isMounted) {
        this.setState({ floorMapError: false, floorMapUrl: floorMapUrl });
      }
    } catch (error) {
      if (this._isMounted) {
        this.setState({ floorMapError: true, floorMapUrl: '' });
      }
    }
  };

  componentDidMount() {
    this._isMounted = true; // Set the flag to true when the component mounts
    const { isSignageMode, signageModeData, isPeopleFormatCount, areaLegend, peopleCountThresholds } =
      this.props;
    if (isSignageMode) {
      const { data, floor } = signageModeData;
      const { dataPeopleCounts } = data;
      let lastUpdateTs = dataPeopleCounts?.[0]?.timestamp ?? '';
      if (lastUpdateTs) {
        lastUpdateTs = format(new Date(lastUpdateTs), 'M月d日 HH:mm');
      }
      this.setState({
        data: dataPeopleCounts,
        floor,
        lastUpdateTs,
        floorMapUrl: this.getFloorMapUrl(floor),
        isPeopleFormatCount,
        areaLegend,
        peopleCountThresholds: peopleCountThresholds || null,
      });
    } else {
      const { querystring } = this.props;
      const floor = querystring?.[QUERY_STRING_STORE.SELECT_BOX_FLOOR]?.[0];
      if (floor) {
        this.setFloorMap(floor);
        this.setState({ floor }, () => {
          this.fetchDataByFloorId();
        });
      }
    }
  }

  componentWillUnmount() {
    this._isMounted = false; // Set the flag to false when the component unmounts
  }

  componentDidUpdate(prevProps) {
    const {
      querystring,
      isSignageMode,
      signageModeData,
      isPeopleFormatCount,
      areaLegend,
      peopleCountThresholds,
    } = this.props;
    const {
      querystring: prevQuerystring,
      signageModeData: prevSignageModeData,
      isPeopleFormatCount: prevIsPeopleFormatCount,
      areaLegend: prevAreaLegend,
      peopleCountThresholds: prevPeopleCountThresholds,
    } = prevProps;
    if (isSignageMode) {
      const { data, floor } = signageModeData;
      const { data: prevData } = prevSignageModeData;
      const { dataPeopleCounts } = data;
      const isSignageModeDataChanged = !isEqual(data, prevData);
      const isLegendChanged =
        !isEqual(areaLegend, prevAreaLegend) ||
        isPeopleFormatCount !== prevIsPeopleFormatCount ||
        !isEqual(peopleCountThresholds, prevPeopleCountThresholds);
      if (isSignageModeDataChanged || isLegendChanged) {
        const nextState = {
          isPeopleFormatCount,
          areaLegend,
          peopleCountThresholds: peopleCountThresholds || null,
        };

        if (isSignageModeDataChanged) {
          let lastUpdateTs = dataPeopleCounts[0]?.timestamp ?? '';
          if (lastUpdateTs) {
            lastUpdateTs = format(new Date(lastUpdateTs), 'MM月dd日 - HH:mm');
          }
          Object.assign(nextState, {
            data: dataPeopleCounts,
            floor,
            floorMapUrl: this.getFloorMapUrl(floor),
            lastUpdateTs,
          });
        }

        this.setState(nextState);
      }
    } else {
      const floor = querystring?.[QUERY_STRING_STORE.SELECT_BOX_FLOOR]?.[0];
      const tenant = querystring?.[QUERY_STRING_STORE.SELECT_BOX_TENANT]?.[0];
      const prevFloor =
        prevQuerystring?.[QUERY_STRING_STORE.SELECT_BOX_FLOOR]?.[0];
      const prevTenant =
        prevQuerystring?.[QUERY_STRING_STORE.SELECT_BOX_TENANT]?.[0];
      const isFloorChanged = floor !== prevFloor;
      const isTenantChanged = tenant !== prevTenant;
      if (isFloorChanged || isTenantChanged) {
        this.setFloorMap(floor);
        this.setState({ floor, tenant }, () => {
          this.fetchDataByFloorId();
        });
      }
    }
  }

  fetchDataByFloorId = async () => {
    const { floor, tenant } = this.state;
    this.setState({ isLoading: true });
    let dataFloorMap = await getPeopleCountsAreaByFloorId({
      floorId: floor,
    });

    let isPeopleFormatCount = false;
    let areaLegend = [];
    const dataPeopleCountFormat = await fetchPeopleCountFormat({
      tenantId: tenant,
    });
    const peopleCountThresholds = await fetchPeopleCountThresholds({
      tenantId: tenant,
    });

    if (
      dataPeopleCountFormat &&
      dataPeopleCountFormat[0]?.people_counts_format === 'count'
    ) {
      isPeopleFormatCount = true;
    }

    let peopleCountThresholdsData = null;
    if (peopleCountThresholds && peopleCountThresholds[0]) {
      const { good, fair, bad, poor } = peopleCountThresholds[0];
      peopleCountThresholdsData = { good, fair, bad, poor };
      
      // Use % symbol only when format is percentage, not count
      const percentSymbol = isPeopleFormatCount ? '' : '%';
      
      areaLegend = [
        {
          label: COMMON_TEXT.EMPTY,
          color: '#00B573',
          value: `~ ${good - 1}${percentSymbol}`,
        },
        {
          label: '',
          color: '#ADDE61',
          value: `${good}${percentSymbol} ~ ${fair - 1}${percentSymbol}`,
        },
        {
          label: '',
          color: '#FFE135',
          value: `${fair}${percentSymbol} ~ ${bad - 1}${percentSymbol}`,
        },
        {
          label: '',
          color: '#FFA14F',
          value: `${bad}${percentSymbol} ~ ${poor - 1}${percentSymbol}`,
        },
        {
          label: COMMON_TEXT.CROWDED,
          color: '#E16A7C',
          value: `${poor}${percentSymbol} ~`,
        },
      ];
    }

    if (!dataFloorMap) {
      dataFloorMap = [];
    }

    const updatedFloorMapData = dataFloorMap.map(item => {
      const { position_x, position_y, position_x1, position_y1 } = item;
      const { pointC, width, height } = calculateRectangle({
        positionX: position_x,
        positionY: position_y,
        positionX1: position_x1,
        positionY1: position_y1,
      });
      const { x, y } = transformCoordinateBottomLeftToTopLeft({
        x: pointC.x,
        y: pointC.y,
      });
      item.rect = {
        top: y,
        left: x,
        width,
        height,
      };
      return item;
    });

    let lastUpdateTs = updatedFloorMapData[0]?.timestamp ?? '';
    if (lastUpdateTs) {
      lastUpdateTs = format(new Date(lastUpdateTs), 'MM月dd日 - HH:mm');
    }
    this.setState({
      data: updatedFloorMapData,
      isLoading: false,
      lastUpdateTs,
      isPeopleFormatCount,
      areaLegend,
      peopleCountThresholds: peopleCountThresholdsData,
    });
  };

  renderMessageError = () => {
    const { floor } = this.state;
    if (!floor) {
      return (
        <Message
          severity="error"
          text={COMMON_TEXT.FLOOR_NOT_SELECTED_PLEASE_SELECT}
        />
      );
    }
  };

  renderRefreshButton() {
    const { isLoading, floor } = this.state;
    return (
      <Button
        icon="pi pi-refresh"
        loading={isLoading}
        disabled={!floor}
        className="btn-refresh"
        severity="info"
        rounded
        text
        aria-label={COMMON_TEXT.REFRESH_BUTTON_TEXT}
        onClick={() => this.fetchDataByFloorId()}
      />
    );
  }

  AreaMarkerViewOnly = props => {
    const { isPeopleFormatCount, peopleCountThresholds } = this.state;
    
    return (
      <AreaMarker
        {...props}
        isViewOnly={true}
        isPeopleFormatCount={isPeopleFormatCount}
        thresholds={peopleCountThresholds}
        updateSelectedAreaMarkerId={() => { }}
      ></AreaMarker>
    );
  };

  infoButtonOnClick = e => {
    if (this.tooltipRef.current) {
      this.tooltipRef.current.show(e);
    }
  };

  renderLegendContent = areaLegend => {
    if (!areaLegend) {
      return;
    }

    const { isSignageMode } = this.props;

    if (isSignageMode) {
      // Use SVG legend for signage mode
      // return <ThresholdLegendSVG />;
      return <ThresholdLegend areaLegend={areaLegend} />;
    }

    // Regular mode legend (existing code)
    return (
      <div className="legend-container">
        <div className="legend-header">
          <span>{COMMON_TEXT.PEOPLE_COUNTS_LEGEND}</span>
        </div>
        <div className="legend-content">
          <div className="legend-header-t">
            {areaLegend.map((item, index) => {
              const { color, label } = item;
              const isHaveLabel = label ? true : false;
              const marginTop =
                index === 1 ? '28px' : index === 2 ? '14px' : '';

              return (
                <div className="item-row" key={`legend-item-row-${index}`}>
                  <div className="item-name-container">
                    <div
                      className="item-color"
                      style={{ backgroundColor: color }}
                    ></div>
                    {isHaveLabel && (
                      <div className="item-name">
                        <span>{label}</span>
                      </div>
                    )}
                    {!isHaveLabel && (
                      <div>
                        <div
                          className="menu-block1"
                          style={{
                            backgroundColor: color,
                            marginTop: marginTop,
                          }}
                        ></div>
                        <div
                          className="menu-block2"
                          style={{ backgroundColor: color }}
                        ></div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="legend-header-t">
            {areaLegend.map((item, index) => {
              const { value } = item;
              return (
                <div className="item-row" key={`legend-item-row-${index}`}>
                  <div className="item-value">
                    <span>{value}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  render() {
    const {
      data: floorMapData,
      isLoading,
      floorMapUrl,
      floorMapError,
      lastUpdateTs,
      areaLegend,
    } = this.state;
    const { isSignageMode, CurrentTimeComponent } = this.props;

    if (isSignageMode) {
      // New signage mode layout
      return (
        <div className="config-container custom-config-container people-counts signage-mode">
          <div className="config-content">
            <div className="main-container">
              {/* Left Sidebar */}
              <div className="signage-sidebar">
                <div className="signage-title">人数検知</div>
                <div className="signage-update-info">
                  更新日時 {lastUpdateTs || '3月21日 15:40'}
                </div>

                {/* Legend */}
                {this.renderLegendContent(areaLegend)}

                {/* Time and Date */}
                <div className="signage-time-container">
                  <div className="signage-date">
                    <span className="date-number"><CurrentTimeComponent format="yyyy" /></span>年
                    <span className="date-number"><CurrentTimeComponent format="M" /></span>月
                    <span className="date-number"><CurrentTimeComponent format="d" /></span>日
                    (<CurrentTimeComponent format="E" />)
                  </div>
                  <div className="signage-time">
                    <CurrentTimeComponent format="HH:mm" />
                  </div>
                </div>
              </div>

              {/* Main Content Area */}
              <div className="signage-main-content">
                {isLoading ? (
                  <div className="loading-card">
                    <LoadingSpinner backgroundColor="#fff" />
                  </div>
                ) : (
                  <ImageAreaMarker
                    bufferLeft={0}
                    bufferTop={0}
                    src={floorMapUrl}
                    areaMarkerComponent={this.AreaMarkerViewOnly}
                    markers={[]}
                    areaMarkers={floorMapError ? [] : floorMapData || []}
                    extraClass="custom-image-marker"
                    onAreaMarkersChange={() => {}}
                  />
                )}

                {floorMapError && (
                  <Message
                    severity="error"
                    text={COMMON_TEXT.CANNOT_LOAD_FLOORMAP}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Bottom Notice Section - This will be handled by SignagePlay component */}
        </div>
      );
    }

    // Regular mode layout (existing code)
    return (
      <div
        className={`config-container custom-config-container people-counts`}
      >
        <div className="config-title-container people-counts-header">
          <div className="left-section" />
          <div className="right-section align-items-center">
            <div className="last-updated-ts">
              {COMMON_TEXT.LAST_UPDATE_TS}: {lastUpdateTs || '--'}
            </div>
            {this.renderRefreshButton()}
          </div>
        </div>
        <div className="config-content">
          {this.renderMessageError() || (
            <>
              <div className="main-container">
                {isLoading ? (
                  <div className="loading-card">
                    <LoadingSpinner backgroundColor="#fff" />
                  </div>
                ) : (
                  <>
                    <ImageAreaMarker
                      bufferLeft={0}
                      bufferTop={0}
                      src={floorMapUrl}
                      areaMarkerComponent={this.AreaMarkerViewOnly}
                      markers={[]}
                      areaMarkers={floorMapError ? [] : floorMapData || []}
                      extraClass=""
                      onAreaMarkersChange={() => {}}
                    />

                    {floorMapError && (
                      <Message
                        severity="error"
                        text={COMMON_TEXT.CANNOT_LOAD_FLOORMAP}
                      />
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
}

PeopleCounts.propTypes = {};
export default PeopleCounts;
